export const FHPL_DURATION_UNITS = ["Days", "Month", "Year"] as const;
export type FhplDurationUnit = (typeof FHPL_DURATION_UNITS)[number];

export type FhplDurationField = {
  value?: string | number | null;
  unit?: FhplDurationUnit | null;
};

export type FhplPreAuthFormValues = {
  patientName?: string;
  gender?: string;
  relationship?: string;
  dateOfBirth?: string | null;
  age?: string | number | null;
  ageUnit?: FhplDurationUnit | null;
  patientMobile?: string;
  attendingRelativeMobile?: string;
  policyNumber?: string;
  familyPhysicianName?: string;
  physicianMobile?: string;
  otherMediclaim?: boolean | string | null;
  otherInsuranceCompanyName?: string;
  otherPolicyNo?: string;

  treatingDoctorName?: string;
  doctorMobileNo?: string;
  probableAdmissionDate?: string | null;
  classOfAccommodation?: string;
  estimatedDays?: string | number | null;
  dateOfFirstConsultation?: string | null;
  presentComplaints?: string;
  ailmentDuration?: string | number | null;
  ailmentDurationUnit?: FhplDurationUnit | null;
  pastHistoryPresentAilment?: string;
  clinicalFindingBp?: string;
  clinicalFindingPulseRate?: string;
  clinicalFindingTemperature?: string;
  clinicalFindingCardioVascular?: string;
  clinicalFindingPerAbdomen?: string;
  clinicalFindingRespiratoryRate?: string;

  primaryDiagnosis?: string;
  proposedLineOfTreatment?: string;
  surgeryOrMedicalManagement?: string;
  investigations?: string;
  icd10PcsCode?: string;
  icd10Code?: string;
  routeOfAdministration?: string;
  admissionType?: string;
  noOfSessions?: string | number | null;
  clinicalFindingsOthers?: string;
  isRta?: boolean | string | null;
  howDidInjuryOccur?: string;
  dateOfInjury?: string | null;
  reportedToPolice?: boolean | string | null;
  firNo?: string;
  substanceAbuse?: boolean | string | null;
  testConducted?: boolean | string | null;
  isMaternity?: boolean | string | null;
  obstetricG?: string | number | null;
  obstetricP?: string | number | null;
  obstetricL?: string | number | null;
  obstetricA?: string | number | null;
  obstetricD?: string | number | null;
  lmp?: string | null;
  dateOfDelivery?: string | null;

  hypertension?: FhplDurationField;
  diabetesMellitus?: FhplDurationField;
  ischemicHeartDisease?: FhplDurationField;
  copdBronchialAsthma?: FhplDurationField;
  cancer?: FhplDurationField;
  alcoholism?: FhplDurationField;
  drugAbuse?: FhplDurationField;
  hivOrStd?: FhplDurationField;
  smoking?: FhplDurationField;
  tobaccoChewing?: FhplDurationField;
  ghutkaChewing?: FhplDurationField;
  hyperlipidemias?: FhplDurationField;
  osteoarthritis?: FhplDurationField;
  anyOtherDisease?: string;
  pastHistoryOthers?: string;

  perDayRoomRent?: string | number | null;
  perDayIcuCharges?: string | number | null;
  investigationsCharge?: string | number | null;
  consultationCharges?: string | number | null;
  medicinesConsumables?: string | number | null;
  allInclusivePackage?: string | number | null;
  professionalFeeSurgeon?: string | number | null;
  otCharges?: string | number | null;
  costOfImplants?: string | number | null;
  totalEstimatedCost?: string | number | null;

  documentsList?: any[];
  claimId?: string | null;
};

const toStr = (value: string | number | boolean | null | undefined): string | null => {
  if (value === null || value === undefined || value === "") return null;
  return String(value);
};

const formatYesNo = (value?: boolean | string | null): string | null => {
  if (value === true || value === "Yes") return "Yes";
  if (value === false || value === "No") return "No";
  return null;
};

const parseYesNo = (value?: string | null): boolean | undefined => {
  if (value === "Yes") return true;
  if (value === "No") return false;
  return undefined;
};

const serializeDurationField = (field?: FhplDurationField | null) => {
  if (!field?.value && field?.value !== 0) return null;
  return {
    value: toStr(field.value),
    unit: field.unit || "Days",
  };
};

const parseDurationField = (raw: unknown): FhplDurationField => {
  if (!raw || typeof raw !== "object") return { unit: "Days" };
  const record = raw as Record<string, unknown>;
  return {
    value: record.value as string | number | null | undefined,
    unit: (record.unit as FhplDurationUnit) || "Days",
  };
};

const buildFhplExtendedJson = (formValues: FhplPreAuthFormValues) => ({
  fhpl: true,
  patientDetails: {
    patientName: formValues.patientName,
    gender: formValues.gender,
    relationship: formValues.relationship,
    dateOfBirth: formValues.dateOfBirth,
    age: toStr(formValues.age),
    ageUnit: formValues.ageUnit || "Year",
    patientMobile: formValues.patientMobile,
    attendingRelativeMobile: formValues.attendingRelativeMobile,
    familyPhysicianName: formValues.familyPhysicianName,
    physicianMobile: formValues.physicianMobile,
    otherMediclaim: formatYesNo(formValues.otherMediclaim),
    otherInsuranceCompanyName: formValues.otherInsuranceCompanyName,
    otherPolicyNo: formValues.otherPolicyNo,
  },
  hospitalization: {
    probableAdmissionDate: formValues.probableAdmissionDate,
    dateOfFirstConsultation: formValues.dateOfFirstConsultation,
    presentComplaints: formValues.presentComplaints,
    ailmentDuration: serializeDurationField({
      value: formValues.ailmentDuration,
      unit: formValues.ailmentDurationUnit,
    }),
    pastHistoryPresentAilment: formValues.pastHistoryPresentAilment,
    clinicalFindings: {
      bp: formValues.clinicalFindingBp,
      pulseRate: formValues.clinicalFindingPulseRate,
      temperature: formValues.clinicalFindingTemperature,
      cardioVascular: formValues.clinicalFindingCardioVascular,
      perAbdomen: formValues.clinicalFindingPerAbdomen,
      respiratoryRate: formValues.clinicalFindingRespiratoryRate,
      others: formValues.clinicalFindingsOthers,
    },
  },
  diagnosis: {
    primaryDiagnosis: formValues.primaryDiagnosis,
    proposedLineOfTreatment: formValues.proposedLineOfTreatment,
    surgeryOrMedicalManagement: formValues.surgeryOrMedicalManagement,
    investigations: formValues.investigations,
    icd10PcsCode: formValues.icd10PcsCode,
    icd10Code: formValues.icd10Code,
    routeOfAdministration: formValues.routeOfAdministration,
    noOfSessions: toStr(formValues.noOfSessions),
    isRta: formatYesNo(formValues.isRta),
    howDidInjuryOccur: formValues.howDidInjuryOccur,
    dateOfInjury: formValues.dateOfInjury,
    reportedToPolice: formatYesNo(formValues.reportedToPolice),
    firNo: formValues.firNo,
    substanceAbuse: formatYesNo(formValues.substanceAbuse),
    testConducted: formatYesNo(formValues.testConducted),
    isMaternity: formatYesNo(formValues.isMaternity),
    obstetricHistory: {
      g: toStr(formValues.obstetricG),
      p: toStr(formValues.obstetricP),
      l: toStr(formValues.obstetricL),
      a: toStr(formValues.obstetricA),
      d: toStr(formValues.obstetricD),
    },
    lmp: formValues.lmp,
    dateOfDelivery: formValues.dateOfDelivery,
  },
  pastHistory: {
    hypertension: serializeDurationField(formValues.hypertension),
    diabetesMellitus: serializeDurationField(formValues.diabetesMellitus),
    ischemicHeartDisease: serializeDurationField(formValues.ischemicHeartDisease),
    copdBronchialAsthma: serializeDurationField(formValues.copdBronchialAsthma),
    cancer: serializeDurationField(formValues.cancer),
    alcoholism: serializeDurationField(formValues.alcoholism),
    drugAbuse: serializeDurationField(formValues.drugAbuse),
    hivOrStd: serializeDurationField(formValues.hivOrStd),
    smoking: serializeDurationField(formValues.smoking),
    tobaccoChewing: serializeDurationField(formValues.tobaccoChewing),
    ghutkaChewing: serializeDurationField(formValues.ghutkaChewing),
    hyperlipidemias: serializeDurationField(formValues.hyperlipidemias),
    osteoarthritis: serializeDurationField(formValues.osteoarthritis),
    anyOtherDisease: formValues.anyOtherDisease,
    others: formValues.pastHistoryOthers,
  },
  billing: {
    perDayRoomRent: toStr(formValues.perDayRoomRent),
    perDayIcuCharges: toStr(formValues.perDayIcuCharges),
    investigationsCharge: toStr(formValues.investigationsCharge),
    consultationCharges: toStr(formValues.consultationCharges),
    medicinesConsumables: toStr(formValues.medicinesConsumables),
    allInclusivePackage: toStr(formValues.allInclusivePackage),
    professionalFeeSurgeon: toStr(formValues.professionalFeeSurgeon),
    otCharges: toStr(formValues.otCharges),
    costOfImplants: toStr(formValues.costOfImplants),
    totalEstimatedCost: toStr(formValues.totalEstimatedCost),
  },
});

const parseFhplExtendedJson = (raw: unknown) => {
  if (!raw) return null;
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (typeof raw === "object") return raw as Record<string, unknown>;
  return null;
};

export const buildFhplCasePreAuthOthersRequest = (formValues: FhplPreAuthFormValues) => {
  const extended = buildFhplExtendedJson(formValues);

  return {
    admissionDate: formValues.probableAdmissionDate || null,
    admissionType: formValues.admissionType || null,
    diagnosis: formValues.primaryDiagnosis || formValues.presentComplaints || null,
    doctorName: formValues.treatingDoctorName || null,
    doctorMobileNo: formValues.doctorMobileNo || null,
    policyNumber: toStr(formValues.policyNumber),
    doctorContact: toStr(formValues.patientMobile),
    requestedAmount:
      formValues.totalEstimatedCost != null
        ? String(formValues.totalEstimatedCost)
        : null,
    lengthOfStay:
      formValues.estimatedDays != null ? String(formValues.estimatedDays) : null,
    roomCategory: formValues.classOfAccommodation || null,
    investigationCharge: toStr(formValues.investigationsCharge),
    roomRent: toStr(formValues.perDayRoomRent),
    doctorCharge: toStr(formValues.consultationCharges),
    medicineCharge: toStr(formValues.medicinesConsumables),
    packageRate: toStr(formValues.allInclusivePackage),
    implantForPpn: toStr(formValues.costOfImplants),
    otherCharge: toStr(formValues.professionalFeeSurgeon),
    procedure: formValues.surgeryOrMedicalManagement || null,
    speciality: formValues.routeOfAdministration || null,
    claimId: formValues.claimId || null,
    noOfDaysAndUnits: JSON.stringify(extended),
  };
};

export const mapFhplPreAuthOthersToForm = (others: any): FhplPreAuthFormValues => {
  if (!others) return {};

  const extended =
    parseFhplExtendedJson(others?.noOfDaysAndUnits) ||
  parseFhplExtendedJson(others?.fhplExtended);

  const patientDetails = (extended?.patientDetails || {}) as Record<string, unknown>;
  const hospitalization = (extended?.hospitalization || {}) as Record<string, unknown>;
  const diagnosis = (extended?.diagnosis || {}) as Record<string, unknown>;
  const pastHistory = (extended?.pastHistory || {}) as Record<string, unknown>;
  const billing = (extended?.billing || {}) as Record<string, unknown>;
  const clinicalFindings = (hospitalization.clinicalFindings || {}) as Record<string, unknown>;
  const obstetricHistory = (diagnosis.obstetricHistory || {}) as Record<string, unknown>;
  const ailmentDuration = parseDurationField(hospitalization.ailmentDuration);

  return {
    patientName: (patientDetails.patientName as string) || undefined,
    gender: (patientDetails.gender as string) || undefined,
    relationship: (patientDetails.relationship as string) || undefined,
    dateOfBirth: (patientDetails.dateOfBirth as string) || undefined,
    age: patientDetails.age as string | number | null | undefined,
    ageUnit: (patientDetails.ageUnit as FhplDurationUnit) || "Year",
    patientMobile: (patientDetails.patientMobile as string) || others?.doctorContact || undefined,
    attendingRelativeMobile: patientDetails.attendingRelativeMobile as string | undefined,
    policyNumber: others?.policyNumber || (patientDetails.policyNumber as string) || undefined,
    familyPhysicianName: patientDetails.familyPhysicianName as string | undefined,
    physicianMobile: patientDetails.physicianMobile as string | undefined,
    otherMediclaim: parseYesNo(patientDetails.otherMediclaim as string),
    otherInsuranceCompanyName: patientDetails.otherInsuranceCompanyName as string | undefined,
    otherPolicyNo: patientDetails.otherPolicyNo as string | undefined,

    treatingDoctorName: others?.doctorName || undefined,
    doctorMobileNo: others?.doctorMobileNo || undefined,
    probableAdmissionDate: others?.admissionDate || (hospitalization.probableAdmissionDate as string),
    classOfAccommodation: others?.roomCategory || undefined,
    estimatedDays: others?.lengthOfStay || undefined,
    dateOfFirstConsultation: hospitalization.dateOfFirstConsultation as string | undefined,
    presentComplaints: hospitalization.presentComplaints as string | undefined,
    ailmentDuration: ailmentDuration.value,
    ailmentDurationUnit: ailmentDuration.unit,
    pastHistoryPresentAilment: hospitalization.pastHistoryPresentAilment as string | undefined,
    clinicalFindingBp: clinicalFindings.bp as string | undefined,
    clinicalFindingPulseRate: clinicalFindings.pulseRate as string | undefined,
    clinicalFindingTemperature: clinicalFindings.temperature as string | undefined,
    clinicalFindingCardioVascular: clinicalFindings.cardioVascular as string | undefined,
    clinicalFindingPerAbdomen: clinicalFindings.perAbdomen as string | undefined,
    clinicalFindingRespiratoryRate: clinicalFindings.respiratoryRate as string | undefined,

    primaryDiagnosis: others?.diagnosis || (diagnosis.primaryDiagnosis as string) || undefined,
    proposedLineOfTreatment: diagnosis.proposedLineOfTreatment as string | undefined,
    surgeryOrMedicalManagement: others?.procedure || (diagnosis.surgeryOrMedicalManagement as string),
    investigations: diagnosis.investigations as string | undefined,
    icd10PcsCode: diagnosis.icd10PcsCode as string | undefined,
    icd10Code: diagnosis.icd10Code as string | undefined,
    routeOfAdministration: others?.speciality || (diagnosis.routeOfAdministration as string),
    admissionType: others?.admissionType || undefined,
    noOfSessions: diagnosis.noOfSessions as string | number | null | undefined,
    clinicalFindingsOthers: clinicalFindings.others as string | undefined,
    isRta: parseYesNo(diagnosis.isRta as string),
    howDidInjuryOccur: diagnosis.howDidInjuryOccur as string | undefined,
    dateOfInjury: diagnosis.dateOfInjury as string | undefined,
    reportedToPolice: parseYesNo(diagnosis.reportedToPolice as string),
    firNo: diagnosis.firNo as string | undefined,
    substanceAbuse: parseYesNo(diagnosis.substanceAbuse as string),
    testConducted: parseYesNo(diagnosis.testConducted as string),
    isMaternity: parseYesNo(diagnosis.isMaternity as string),
    obstetricG: obstetricHistory.g as string | number | null | undefined,
    obstetricP: obstetricHistory.p as string | number | null | undefined,
    obstetricL: obstetricHistory.l as string | number | null | undefined,
    obstetricA: obstetricHistory.a as string | number | null | undefined,
    obstetricD: obstetricHistory.d as string | number | null | undefined,
    lmp: diagnosis.lmp as string | undefined,
    dateOfDelivery: diagnosis.dateOfDelivery as string | undefined,

    hypertension: parseDurationField(pastHistory.hypertension),
    diabetesMellitus: parseDurationField(pastHistory.diabetesMellitus),
    ischemicHeartDisease: parseDurationField(pastHistory.ischemicHeartDisease),
    copdBronchialAsthma: parseDurationField(pastHistory.copdBronchialAsthma),
    cancer: parseDurationField(pastHistory.cancer),
    alcoholism: parseDurationField(pastHistory.alcoholism),
    drugAbuse: parseDurationField(pastHistory.drugAbuse),
    hivOrStd: parseDurationField(pastHistory.hivOrStd),
    smoking: parseDurationField(pastHistory.smoking),
    tobaccoChewing: parseDurationField(pastHistory.tobaccoChewing),
    ghutkaChewing: parseDurationField(pastHistory.ghutkaChewing),
    hyperlipidemias: parseDurationField(pastHistory.hyperlipidemias),
    osteoarthritis: parseDurationField(pastHistory.osteoarthritis),
    anyOtherDisease: pastHistory.anyOtherDisease as string | undefined,
    pastHistoryOthers: pastHistory.others as string | undefined,

    perDayRoomRent: (billing.perDayRoomRent ?? others?.roomRent) as string | number | null | undefined,
    perDayIcuCharges: billing.perDayIcuCharges as string | number | null | undefined,
    investigationsCharge: (billing.investigationsCharge ?? others?.investigationCharge) as
      | string
      | number
      | null
      | undefined,
    consultationCharges: (billing.consultationCharges ?? others?.doctorCharge) as
      | string
      | number
      | null
      | undefined,
    medicinesConsumables: (billing.medicinesConsumables ?? others?.medicineCharge) as
      | string
      | number
      | null
      | undefined,
    allInclusivePackage: (billing.allInclusivePackage ?? others?.packageRate) as
      | string
      | number
      | null
      | undefined,
    professionalFeeSurgeon: (billing.professionalFeeSurgeon ?? others?.otherCharge) as
      | string
      | number
      | null
      | undefined,
    otCharges: billing.otCharges as string | number | null | undefined,
    costOfImplants: (billing.costOfImplants ?? others?.implantForPpn) as
      | string
      | number
      | null
      | undefined,
    totalEstimatedCost: (billing.totalEstimatedCost ?? others?.requestedAmount) as
      | string
      | number
      | null
      | undefined,
  };
};
